import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import keycloak, { isDevelopment, KEYCLOAK_CONFIG } from "../keycloak";
import { styles } from "../styles";

interface ConditionalAuthProviderProps {
  children: React.ReactNode;
}

const LoadingComponent = () => {
  const { t } = useTranslation();
  return (
    <div className={styles.auth.loading.container}>
      <div className={styles.auth.loading.wrapper}>
        <div className={styles.auth.loading.spinner}></div>
        <p className={styles.auth.loading.text}>
          {t("auth.initializingAuthentication")}
        </p>
        {isDevelopment && (
          <div className={styles.auth.debugInfo.container}>
            <p className={styles.auth.debugInfo.text}>
              <strong>{t("auth.developmentMode")}</strong>{" "}
              {t("auth.checkingKeycloakConnectivity")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export const ConditionalAuthProvider: React.FC<
  ConditionalAuthProviderProps
> = ({ children }) => {
  const { t } = useTranslation();
  const [useKeycloak, setUseKeycloak] = useState<boolean | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    // Test Keycloak connectivity
    const testKeycloakConnection = async () => {
      try {
        // Try to reach the Keycloak server
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // Reduced to 3 seconds for faster response

        const response = await fetch(
          `${KEYCLOAK_CONFIG.url}/realms/${KEYCLOAK_CONFIG.realm}`,
          {
            method: "GET",
            signal: controller.signal,
            mode: "cors",
          },
        );

        clearTimeout(timeoutId);

        if (response.ok) {
          console.log(
            "Keycloak server is reachable, using Keycloak authentication",
          );
          setUseKeycloak(true);
        } else {
          throw new Error(`Server responded with status: ${response.status}`);
        }
      } catch (error) {
        console.warn("Keycloak server connectivity test failed:", error);
        setConnectionError(
          error instanceof Error ? error.message : "Unknown error",
        );

        // Still try to use Keycloak even if connectivity test fails
        // This allows for network issues during test but working auth flow
        console.log(
          "Proceeding with Keycloak despite connectivity test failure",
        );
        setUseKeycloak(true);
      }
    };

    testKeycloakConnection();
  }, []);

  // Still testing connectivity
  if (useKeycloak === null) {
    return <LoadingComponent />;
  }

  // Use Keycloak authentication if connectivity test passed
  if (useKeycloak) {
    return (
      <ReactKeycloakProvider
        authClient={keycloak}
        initOptions={{
          onLoad: "login-required", // Always force login to avoid storage access issues
          scope: "openid profile email organization", // Request user profile, email and organization claims
          checkLoginIframe: false, // Disable iframe checks to avoid storage issues
          enableLogging: isDevelopment,
          // Disable all iframe and silent SSO features that cause storage access issues
          silentCheckSsoRedirectUri: undefined,
          checkLoginIframeInterval: 0,
          // Add token minimum validity (5 minutes)
          minValidity: 300,
          // Use standard flow to avoid storage access issues
          flow: "standard",
          // Disable third-party cookies and browser state
          enableBrowserState: false,
          // Use fragment response mode (works better with storage restrictions)
          responseMode: "fragment",
          // Use PKCE for better security without storage issues
          pkceMethod: "S256",
          // Disable adapter state check to avoid localStorage issues
          adapter: "default",
          // Don't try to access storage on initialization
          checkLoginIframeOptions: {
            enable: false,
          },
        }}
        LoadingComponent={<LoadingComponent />}
        onEvent={(event, error) => {
          console.log("🔐 Keycloak event:", event, error);
          if (event === "onReady") {
            console.log("🔐 Keycloak ready - initialization complete");
            // Mark that we've attempted auto-login
            localStorage.setItem("keycloak-auto-login-attempted", "true");
            // Dispatch custom event that SAPDataProvider can listen to
            window.dispatchEvent(new CustomEvent("keycloak-ready"));
          }
          if (event === "onAuthSuccess") {
            console.log("🔐 Keycloak authentication successful");
            // Clear the auto-login flag on successful auth
            localStorage.removeItem("keycloak-auto-login-attempted");
          }
          if (event === "onTokenExpired") {
            console.log(
              "🔐 Keycloak token expired — library will auto-refresh",
            );
            // Note: @react-keycloak/core already calls updateToken(5) internally.
            // Do NOT call it again here to avoid race conditions.
          }
          if (event === "onAuthRefreshSuccess") {
            console.log("🔐 Keycloak token refresh successful");
            // Update NATS with fresh token so reconnections use it
            if (keycloak.token) {
              import("../services/NATSClient")
                .then(({ natsClient }) => {
                  natsClient.updateToken(keycloak.token!);
                })
                .catch(() => {
                  /* NATS not yet initialized */
                });
              // Broadcast so other token consumers (e.g. the Live Shift worker,
              // which has its own NATS connection and cannot reach Keycloak) can
              // proactively pick up the fresh token for their reconnections.
              window.dispatchEvent(
                new CustomEvent("keycloak-token-refreshed", {
                  detail: { token: keycloak.token },
                }),
              );
            }
          }
          if (event === "onAuthRefreshError") {
            console.error("🔐 Keycloak token refresh failed:", error);
          }
          if (event === "onInitError" && isDevelopment) {
            console.warn(
              "Keycloak initialization failed - authentication may not work properly",
            );
          }
        }}
        onTokens={(tokens) => {
          console.log(
            "🔐 Keycloak tokens updated - access token length:",
            tokens?.token?.length || 0,
          );
        }}
      >
        {children}
      </ReactKeycloakProvider>
    );
  }

  // Show error if Keycloak is not available
  return (
    <div className={styles.auth.authUnavailable.container}>
      <div className={styles.auth.authUnavailable.wrapper}>
        <div className={styles.auth.authUnavailable.errorIcon.container}>
          <svg
            className={styles.auth.authUnavailable.errorIcon.svg}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className={styles.auth.authUnavailable.title}>
          {t("auth.authServiceUnavailable")}
        </h2>
        <p className={styles.auth.authUnavailable.description}>
          {t("auth.unableToConnect")}
        </p>
        {connectionError && (
          <p className={styles.auth.authUnavailable.notice}>
            {t("auth.error")} {connectionError}
          </p>
        )}
        <button
          onClick={() => window.location.reload()}
          className={styles.auth.authUnavailable.retryButton}
        >
          {t("auth.retry")}
        </button>
      </div>
    </div>
  );
};
